import { useEffect } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAdminStore } from '../../store/useAdminStore'
import { getMe, logout as apiLogout } from '../../api/admin'

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/pipelines', label: 'Pipeline', icon: '⚙️' },
  { path: '/admin/projects', label: 'Projects', icon: '📁' },
  { path: '/admin/models', label: 'Models', icon: '🤖' },
]

export default function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { admin, isAuthenticated, setAdmin, logout, isLoading, setLoading } = useAdminStore()

  // Check auth on mount
  const { isLoading: authLoading } = useQuery({
    queryKey: ['admin-me'],
    queryFn: async () => {
      try {
        const data = await getMe()
        setAdmin(data)
        return data
      } catch {
        setAdmin(null)
        return null
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  useEffect(() => {
    setLoading(authLoading)
  }, [authLoading, setLoading])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login')
    }
  }, [isLoading, isAuthenticated, navigate])

  const handleLogout = async () => {
    try {
      await apiLogout()
    } catch {
      // Ignore errors
    }
    logout()
    navigate('/admin/login')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-gray-700">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logo.png" alt="Fox Ear" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-lg">Fox Ear</h1>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/admin' && location.pathname.startsWith(item.path))
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg transition ${
                      isActive
                        ? 'bg-orange-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* User */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{admin?.username}</p>
              <p className="text-xs text-gray-400">{admin?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-white transition"
              title="Logout"
            >
              🚪
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
